/*
 * Judge.me reviews — collapse/expand wrapper.
 *
 * Wraps the Judge.me review widget in a native <details> element with a
 * summary header styled to match the "Materials & Sustainability" section
 * (mono-uppercase title + rotating +/- icon), plus average stars and a
 * review count.
 *
 * Auto-state: the section auto-EXPANDS when the product has reviews and
 * stays COLLAPSED when it has none (the empty state + "Write a review"
 * button are still reachable by clicking the header). The auto-state is
 * applied exactly once, after Judge.me finishes hydrating; any manual
 * toggle by the visitor afterward is left untouched.
 *
 * Self-contained IIFE — no imports/exports. Loaded with `defer` on the
 * product template from snippets/scripts.liquid.
 */
(function () {
  'use strict';

  var WRAP_CLASS = 'jdgm-collapse';
  var TITLE = 'REVIEWS & RATINGS';
  var LOAD_TIMEOUT = 10000; // ms — fallback if Judge.me never reports stats
  var APPEAR_TIMEOUT = 15000; // ms — give up waiting for the widget to appear

  /** Locate the Judge.me *review* widget (not the small preview badge). */
  function findWidget() {
    var el =
      document.querySelector('.jdgm-widget.jdgm-review-widget') ||
      document.querySelector('.jdgm-review-widget');
    if (el) return el;
    var revWidg = document.querySelector('.jdgm-rev-widg');
    return revWidg ? revWidg.closest('.jdgm-widget') || revWidg : null;
  }

  /** True once Judge.me has rendered something we can measure. */
  function widgetLoaded(widget) {
    var rw = widget.querySelector('.jdgm-rev-widg') || widget;
    return !!(
      rw.hasAttribute('data-number-of-reviews') ||
      widget.querySelector('.jdgm-rev') ||
      widget.querySelector('.jdgm-rev-widg__summary') ||
      widget.querySelector('.jdgm-no-reviews-text')
    );
  }

  /** Read { count, avg } from Judge.me data attributes, with DOM fallbacks. */
  function readStats(widget) {
    var rw = widget.querySelector('.jdgm-rev-widg') || widget;

    // Count: prefer the true total (data attr → "Based on N reviews" text),
    // fall back to visible cards (note: Judge.me paginates, so this can
    // undercount — only used when nothing better is available).
    var count = parseInt(rw.getAttribute('data-number-of-reviews'), 10);
    if (isNaN(count)) {
      var summaryEl = widget.querySelector('.jdgm-rev-widg__summary-text, .jdgm-rev-widg__summary');
      var m = summaryEl ? (summaryEl.textContent || '').match(/(\d[\d,]*)/) : null;
      if (m) count = parseInt(m[1].replace(/,/g, ''), 10);
    }
    if (isNaN(count)) count = widget.querySelectorAll('.jdgm-rev').length;

    var avg = parseFloat(rw.getAttribute('data-average-rating'));
    if (isNaN(avg)) {
      var avgEl = widget.querySelector('.jdgm-rev-widg__summary-average');
      avg = avgEl ? parseFloat(avgEl.textContent || '') : 0;
    }
    if (isNaN(avg)) avg = 0;

    return { count: count, avg: avg };
  }

  /** Render the average as five star glyphs. */
  function buildStars(avg) {
    var rounded = Math.round(avg);
    var wrap = document.createElement('span');
    wrap.className = 'jdgm-collapse__stars';
    for (var i = 1; i <= 5; i++) {
      var star = document.createElement('span');
      star.className = 'jdgm-collapse__star' + (i <= rounded ? ' is-on' : '');
      star.textContent = '★';
      wrap.appendChild(star);
    }
    return wrap;
  }

  /** (Re)render the stars + count inside an existing summary's meta slot.
      When there are no reviews, the meta stays empty (title only). */
  function renderMeta(meta, stats) {
    meta.textContent = '';
    if (stats.count > 0) {
      meta.appendChild(buildStars(stats.avg));
      var count = document.createElement('span');
      count.className = 'jdgm-collapse__count';
      count.textContent = stats.count + (stats.count === 1 ? ' review' : ' reviews');
      meta.appendChild(count);
    }
  }

  function buildSummary(stats) {
    var summary = document.createElement('summary');
    summary.className = 'jdgm-collapse__summary';

    var left = document.createElement('span');
    left.className = 'jdgm-collapse__left';

    var title = document.createElement('span');
    title.className = 'jdgm-collapse__title';
    title.textContent = TITLE;

    var meta = document.createElement('span');
    meta.className = 'jdgm-collapse__meta';
    renderMeta(meta, stats);

    left.appendChild(title);
    left.appendChild(meta);

    var icon = document.createElement('span');
    icon.className = 'jdgm-collapse__icon';
    icon.setAttribute('aria-hidden', 'true');

    summary.appendChild(left);
    summary.appendChild(icon);
    return summary;
  }

  function init() {
    var widget = findWidget();
    if (!widget || !widget.parentNode) return false;
    if (widget.closest('.' + WRAP_CLASS)) return true; // already wrapped

    var stats = readStats(widget);
    var details = document.createElement('details');
    details.className = WRAP_CLASS;
    var summary = buildSummary(stats);

    // Wrap: put <details> where the widget was, then nest summary + widget.
    widget.parentNode.insertBefore(details, widget);
    details.appendChild(summary);
    details.appendChild(widget);

    var autoStateApplied = false;
    var meta = summary.querySelector('.jdgm-collapse__meta');

    function applyAutoStateOnce() {
      if (autoStateApplied || !widgetLoaded(widget)) return;
      var s = readStats(widget);
      renderMeta(meta, s);
      details.open = s.count > 0;
      autoStateApplied = true;
    }

    // Keep header stats fresh as Judge.me hydrates / re-renders, and apply
    // the auto open/closed state the first time stats become available.
    var observer = new MutationObserver(function () {
      renderMeta(meta, readStats(widget));
      applyAutoStateOnce();
    });
    observer.observe(widget, { childList: true, subtree: true });

    // In case the widget was already loaded before we wrapped it.
    applyAutoStateOnce();

    // Fallback: if Judge.me never reports, default to expanded (show content).
    setTimeout(function () {
      if (!autoStateApplied) {
        renderMeta(meta, readStats(widget));
        details.open = true;
        autoStateApplied = true;
      }
    }, LOAD_TIMEOUT);

    return true;
  }

  function boot() {
    if (init()) return;
    // Judge.me injects the widget asynchronously — wait for it to appear.
    var observer = new MutationObserver(function () {
      if (init()) observer.disconnect();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    setTimeout(function () {
      observer.disconnect();
    }, APPEAR_TIMEOUT);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
